/**
 * Settings — the "More" tab, and a home for the four things that had nowhere to live.
 *
 * All of these used to be crammed into the matches screen's top bar: the theme toggle and
 * "Sign out" sat one mis-tap apart, directly above "New match". Signing out is not
 * destructive — #14 is explicit that it ends a session without touching a ledger, and #13
 * put every ball on disk — but a scorer who hits it by accident on a ground with no signal
 * cannot sign back in, and `screenFor` will then only let him carry on a match already on
 * the device. Moving it behind a tab costs one tap and removes that whole class of afternoon.
 *
 * The theme toggle stays in the top bar of the scoring screen as well as living here. It
 * is the one setting with a *time* attached — the light goes mid-innings — and making a
 * scorer leave the pad to reach it would be exactly the navigation the loop does not have.
 */
import type { AuthUser } from "@sportsos/engine";

import { showsInstallHelpHere } from "../install.ts";
import { Chevron } from "./BottomSheet.tsx";
import { avatarColor, initials } from "./shared.ts";

const CHEVRON = Chevron;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface Props {
  readonly user: AuthUser;
  readonly dark: boolean;
  readonly onToggleTheme: () => void;
  readonly onSignOut: () => void;
  readonly onOpenTeams: () => void;
  readonly onOpenTournaments: () => void;
  readonly onOpenStats: () => void;
  readonly onEditProfile: (field: "phone" | "email") => void;
  /**
   * Apply a waiting service-worker update, or null when there is nothing waiting.
   *
   * Null when no update is waiting, and for every browser with no service-worker support —
   * so this row is absent rather than disabled in the ordinary case.
   */
  readonly onApplyUpdate?: (() => void) | null;
}

export function Settings({
  user,
  dark,
  onToggleTheme,
  onSignOut,
  onOpenTeams,
  onOpenTournaments,
  onOpenStats,
  onEditProfile,
  onApplyUpdate = null,
}: Props): React.ReactElement {
  const color = avatarColor(user.displayName);
  // iOS only, and only in a browser tab. See `install.ts` for why Android is not here yet.
  const offerInstall = showsInstallHelpHere();

  return (
    <div className="app-page">
      {/* ── App bar ── */}
      <header className="ds-appbar">
        <span className="ds-appbar__body">
          <b className="ds-appbar__title" style={{ fontSize: "var(--ds-text-xl)" }}>
            Account
          </b>
        </span>
      </header>

      {/* ── User profile card ── */}
      <article className="ds-card">
        <div style={{ display: "flex", alignItems: "center", gap: "var(--ds-space-4)" }}>
          <span className={`ds-avatar ds-avatar--lg ds-avatar--${color}`}>
            {initials(user.displayName)}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: "block", fontSize: "var(--ds-text-lg)" }}>
              {user.displayName}
            </b>
            {user.email && <span className="ds-caption" style={{ display: "block" }}>{user.email}</span>}
            {user.phone && <span className="ds-caption" style={{ display: "block" }}>{user.phone}</span>}
          </span>
        </div>

        <div style={{ marginTop: "var(--ds-space-4)", display: "flex", flexDirection: "column", gap: "var(--ds-space-2)" }}>
          {!user.email && (
            <button className="ds-list-item" type="button" onClick={() => onEditProfile("email")}
              style={{ padding: "var(--ds-space-3)", borderColor: "var(--ds-warning-500)" }}>
              <span className="ds-list-item__body">
                <b className="ds-list-item__title" style={{ color: "var(--ds-warning-600)" }}>Add email</b>
                <span className="ds-list-item__meta">Needed for password login and recovery</span>
              </span>
              {CHEVRON}
            </button>
          )}
          {!user.phone && (
            <button className="ds-list-item" type="button" onClick={() => onEditProfile("phone")}
              style={{ padding: "var(--ds-space-3)", borderColor: "var(--ds-warning-500)" }}>
              <span className="ds-list-item__body">
                <b className="ds-list-item__title" style={{ color: "var(--ds-warning-600)" }}>Add phone number</b>
                <span className="ds-list-item__meta">Needed for OTP login and team invites</span>
              </span>
              {CHEVRON}
            </button>
          )}
        </div>
      </article>

      {/* ── Cricket section ── */}
      <p className="ds-label" style={{ margin: "var(--ds-space-6) 0 var(--ds-space-2)" }}>
        Cricket
      </p>
      <div className="ds-list">
        <button className="ds-list-item" type="button" onClick={onOpenStats}>
          <span className="ds-iconslot ds-iconslot--green" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" rx="4" />
              <path d="M9 15v-3M12 15V9M15 15v-5" />
            </svg>
          </span>
          <span className="ds-list-item__body">
            <b className="ds-list-item__title">My match stats</b>
            <span className="ds-list-item__meta">Batting, bowling and fielding</span>
          </span>
          {CHEVRON}
        </button>

        <button className="ds-list-item" type="button" onClick={onOpenTeams}>
          <span className="ds-iconslot ds-iconslot--amber" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="9.2" cy="8" r="3.3" />
              <path d="M3 20a6.2 6.2 0 0 1 12.4 0" />
              <path d="M16 5.2a3.3 3.3 0 0 1 0 5.6M17.6 20a6.3 6.3 0 0 0-1.9-4.4" />
            </svg>
          </span>
          <span className="ds-list-item__body">
            <b className="ds-list-item__title">My teams</b>
          </span>
          {CHEVRON}
        </button>

        <button className="ds-list-item" type="button" onClick={onOpenTournaments}>
          <span className="ds-iconslot ds-iconslot--teal" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11M12 14v3M8.5 20h7" />
            </svg>
          </span>
          <span className="ds-list-item__body">
            <b className="ds-list-item__title">My tournaments</b>
          </span>
          {CHEVRON}
        </button>
      </div>

      {/* ── Theme section ── */}
      <div
        className="ds-card ds-card--flat"
        style={{ marginTop: "var(--ds-space-6)", padding: "var(--ds-space-4) var(--ds-space-5)" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--ds-space-3)",
            marginBottom: "var(--ds-space-3)",
          }}
        >
          <span className="ds-iconslot ds-iconslot--magenta" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8.4" />
              <path d="M12 3.6v16.8" />
            </svg>
          </span>
          <span style={{ flex: 1 }}>
            <b style={{ display: "block" }}>Theme</b>
            <span className="ds-caption">
              {dark ? "Dark mode is active" : "Light mode is active"}
            </span>
          </span>
        </div>
        <div className="ds-segmented" role="tablist" style={{ width: "100%" }}>
          <button
            className="ds-segmented__item"
            role="tab"
            aria-selected={!dark}
            onClick={dark ? onToggleTheme : undefined}
            style={{ flex: 1 }}
            type="button"
          >
            Light
          </button>
          <button
            className="ds-segmented__item"
            role="tab"
            aria-selected={dark}
            onClick={!dark ? onToggleTheme : undefined}
            style={{ flex: 1 }}
            type="button"
          >
            Dark
          </button>
        </div>
      </div>

      {/* ── Update waiting ──
          The worker deliberately never applies an update on its own, so this is the only way
          a new build reaches a scorer. Placed here rather than as a banner because the point
          is that updating is something done *between* matches: an "update available" bar over
          the scoring pad is exactly the interruption the no-`skipWaiting` rule exists to
          prevent. Absent entirely when nothing is waiting. */}
      {onApplyUpdate !== null && (
        <div
          className="ds-card ds-card--flat"
          style={{ marginTop: "var(--ds-space-6)", padding: "var(--ds-space-4) var(--ds-space-5)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--ds-space-3)" }}>
            <span className="ds-iconslot ds-iconslot--green" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 20.4a8.4 8.4 0 1 1 8.4-8.4" />
                <path d="M20.4 6v6h-6" />
              </svg>
            </span>
            <span style={{ flex: 1 }}>
              <b style={{ display: "block" }}>Update available</b>
              <span className="ds-caption">Takes a moment. Finish the over first.</span>
            </span>
          </div>
          <button
            className="ds-btn ds-btn--secondary ds-btn--block"
            type="button"
            style={{ marginTop: "var(--ds-space-3)" }}
            onClick={onApplyUpdate}
          >
            Update now
          </button>
        </div>
      )}

      {/* ── Install (iOS only) ──
          Instructions rather than a button, because Safari has no install prompt to offer:
          the whole flow is Share → Add to Home Screen, by hand. Placed below the fold and
          never shown as a banner — a scorer who opens the app twenty minutes before the toss
          should not meet an install nag on the way to the pad. */}
      {offerInstall && (
        <div
          className="ds-card ds-card--flat"
          style={{ marginTop: "var(--ds-space-6)", padding: "var(--ds-space-4) var(--ds-space-5)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--ds-space-3)" }}>
            <span className="ds-iconslot ds-iconslot--azure" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 3.6v11.2M8.4 7.2 12 3.6l3.6 3.6" />
                <path d="M5.4 13.2v5.4a1.8 1.8 0 0 0 1.8 1.8h9.6a1.8 1.8 0 0 0 1.8-1.8v-5.4" />
              </svg>
            </span>
            <span style={{ flex: 1 }}>
              <b style={{ display: "block" }}>Add to Home Screen</b>
              <span className="ds-caption">Open it like an app, with no address bar</span>
            </span>
          </div>
          <p
            className="ds-caption"
            style={{ marginTop: "var(--ds-space-3)", marginBottom: 0 }}
          >
            Tap <b>Share</b> in Safari&rsquo;s toolbar, then <b>Add to Home Screen</b>.
          </p>
        </div>
      )}

      {/* ── Support section ── */}
      <p className="ds-label" style={{ margin: "var(--ds-space-6) 0 var(--ds-space-2)" }}>
        Support
      </p>
      <div className="ds-list">
        <button className="ds-list-item" type="button">
          <span className="ds-iconslot ds-iconslot--green" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8.4" />
              <path d="M9.6 9.4a2.5 2.5 0 1 1 3.2 2.9c-.6.2-.8.7-.8 1.3v.4" />
              <path d="M12 17.1h.01" />
            </svg>
          </span>
          <span className="ds-list-item__body">
            <b className="ds-list-item__title">Help &amp; support</b>
            <span className="ds-list-item__meta">Scoring rules, FAQs, contact us</span>
          </span>
          {CHEVRON}
        </button>
      </div>

      {/* ── Log out ── */}
      <button
        className="ds-btn ds-btn--secondary ds-btn--block"
        style={{ marginTop: "var(--ds-space-6)", color: "var(--ds-danger-600)" }}
        onClick={onSignOut}
        type="button"
      >
        Log out
      </button>

      {/* ── Version ── */}
      <p className="ds-caption" style={{ textAlign: "center", marginTop: "var(--ds-space-3)" }}>
        CricAI.co &middot; v{__APP_VERSION__}
      </p>
    </div>
  );
}
